import * as React from 'react';

import {
    Tooltip
} from '@mui/material';

import { 
    DetailedUserResponse,
    RoleResponse, 
    TenantUserMetadataSetting,
    PortalTextCommon,
    PortalTextSettings,
} from '../../../Models/Api/strongbox.financialportal';

import { FullUserName } from '../../../Utils/UserUtils';
import { StringFormat } from '../../../Utils/StringUtils';
import { InlineStylingValues } from '../../../Utils/Constants';

import { BrandConfig } from '../../../Store/Tenant';

import { SortableTable } from '../../SortableTable/SortableTable';

import { FormatDate, formatStringMDYFullMonth } from '../../../Utils/DateUtils';

import { EditUserModal } from './EditUserModal';
import { ConfirmModal, ConfirmModalType } from '../../ConfirmModal/ConfirmModal';

import { GetUserStatusDescription, GetRoleDescriptionFromId } from './CommonUser';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTrash,
    faPencil,
    faEnvelope,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';

import { ErrorBanner, ErrorState } from '../../ErrorBanner/ErrorBanner';

import '../../Main.scss';

type Props = {
    availableRoles: RoleResponse[];
    users: DetailedUserResponse[];
    loggedInUserExternalId: string;
    brandConfig: BrandConfig;
    onUpdateUser: (user: DetailedUserResponse) => void;
    onDeleteUser: (user: DetailedUserResponse, onComplete?: (errorMsg?: string) => void) => void;
    onReinviteUser: (user: DetailedUserResponse, onComplete: (errorMsg?: string) => void) => void;
    tenantUserMetadataSettings: TenantUserMetadataSetting[];
    commonText?: PortalTextCommon;
    settingsText?: PortalTextSettings;
}

const invitableStatuses = new Set(['Invited', 'PendingInvite']);

export const UserList: React.FC<Props> = (props): React.ReactElement => {
    const {
        tenantUserMetadataSettings,
        settingsText,
        availableRoles,
        onUpdateUser,
        loggedInUserExternalId,
        onDeleteUser,
        commonText,        
        brandConfig,
        onReinviteUser,
        users,
    } = props;

    const [editingUser, setEditingUser] = React.useState<DetailedUserResponse | undefined>(undefined);
    const [confirmDelete, setConfirmDelete] = React.useState<DetailedUserResponse | undefined>(undefined);
    const [errorMsg, setErrorMsg] = React.useState<ErrorState | undefined>(undefined);

    // Will be a mapping for name (id) to label
    const tenantSettingsLabelMap = React.useMemo(() => {
        const result = new Map<string, string>();
        tenantUserMetadataSettings.forEach(m => {
            result.set(m.name, m.label);
        });
        return result;
    }, [tenantUserMetadataSettings]);

    const getRoleDescription = (user: DetailedUserResponse): string => {
        let roles: string[] = [];

        user.resourceRoles.forEach(role => {
            const roleDef = availableRoles.find(roleResponse => roleResponse.id === role.roleId);
            if (!!roleDef) {
                roles.push(GetRoleDescriptionFromId(roleDef.id, settingsText));
            }
        });

        // Put the roles in alphabetical order so they show up the same for every user.  They're not
        // always in the same order in user.resourceRoles
        roles = roles.sort((role1, role2) => role1 === role2 ? 0 : role1 < role2 ? -1 : 1);

        let result = '';
        let needsComma = false;

        roles.forEach(role => {
            result += `${needsComma ? ', ' : ''}${role}`;
            needsComma = true;
        });

        return result;
    }

    const getUserNameCell = (user: DetailedUserResponse): React.ReactElement => {
        const userName = FullUserName(user);

        if (user.metadata.length <= 0) {
            return (<span>{userName}</span>)
        }

        return (
            <div className={'list-box'}>
                <span>{userName}</span>
                <div className={'list-contents'}>
                    {
                        user.metadata.map((m, i) => {
                            const map = tenantSettingsLabelMap.get(m.name);
                            const metadataText = `${map || m.name}: ${m.value}`;
                            return (
                                <span
                                    key={`${userName}-metadata-item-${m.name}-${i}`}
                                >
                                    {metadataText}
                                </span>
                            )
                        })
                    }
                </div>
            </div>
        )
    }

    return (
        <>
            {
                editingUser && (
                    <EditUserModal
                        user={editingUser}
                        availableRoles={availableRoles}
                        onDismissModal={(operation, user) => {
                            if (operation === 'save') {
                                user && onUpdateUser(user);
                            }
                            setEditingUser(undefined);
                        }}
                        loggedInUserExternalId={loggedInUserExternalId}
                    />
                )
            }
            {
                !!confirmDelete &&
                <ConfirmModal
                    msg={StringFormat(settingsText?.templateConfirmDeleteUser || '{0}', confirmDelete.displayName)}
                    title={commonText?.warning || ''}
                    modalType={ConfirmModalType.yesno}
                    onTerminalButton={(yes?: boolean) => {
                        if (!!yes) {
                            onDeleteUser(
                                confirmDelete,
                                (errorMsg) => {
                                    if (!!errorMsg) {
                                        setErrorMsg({
                                            summaryMessage: errorMsg,
                                            severity: 'Error'
                                        });
                                    }
                                }
                            );
                        }
                        setConfirmDelete(undefined);
                    }}
                    commonText={commonText}
                />
            }
            {
                !!errorMsg && (
                    <div className={'sticky-banner-container'}>
                        <ErrorBanner
                            errorState={errorMsg}
                            onDefaultActionButton={() => setErrorMsg(undefined) }
                            commonText={commonText}
                        />
                    </div>
                )
            }
            <SortableTable<DetailedUserResponse>
                brandConfig={brandConfig}
                columns={[
                    {
                        defaultSortOrder: 'none',
                        headerTitle: '',
                        compare: (u1: DetailedUserResponse, u2: DetailedUserResponse) => {
                            return 0;
                        },
                        getRowData: (user: DetailedUserResponse): React.ReactElement => {
                            return (
                                <div
                                    style={{
                                        minWidth: '100px',
                                    }}
                                >
                                    <Tooltip title={`${settingsText?.editUserTooltipPrefix || ''} (${user.displayName})`}>
                                        <FontAwesomeIcon
                                            icon={faPencil}
                                            id={`editIcon${user.id}`}                                            
                                            className={'actionable-icon'}
                                            onClick={() => setEditingUser(user)}
                                            style={{ marginRight: InlineStylingValues.IconSeparator }}
                                        />
                                    </Tooltip>
                                    {
                                        invitableStatuses.has(user.status) && (user.externalAccountId !== loggedInUserExternalId) &&
                                        (
                                            <React.Fragment>
                                                <Tooltip title={`${settingsText?.reinviteUserTooltipPrefix || ''} (${user.displayName})`}>
                                                    <FontAwesomeIcon
                                                        icon={faEnvelope}
                                                        id={`reinviteIcon${user.id}`}                                                        
                                                        className={'actionable-icon'}
                                                        style={{ marginRight: InlineStylingValues.IconSeparator }}
                                                        onClick={() => {
                                                            onReinviteUser(
                                                                user,
                                                                (errorMsg) => {
                                                                    if (!!errorMsg) {
                                                                        setErrorMsg({
                                                                            summaryMessage: errorMsg,
                                                                            severity: 'Error'
                                                                        });
                                                                    } else {
                                                                        setErrorMsg({
                                                                            summaryMessage: settingsText?.userHasBeenReinvited || '',
                                                                            severity: 'Notification'
                                                                        });
                                                                    }
                                                                }
                                                            );
                                                        }}
                                                    />
                                                </Tooltip>
                                            </React.Fragment>
                                        )
                                    }
                                    {
                                        (!invitableStatuses.has(user.status) || (user.externalAccountId === loggedInUserExternalId)) &&
                                        (
                                            <FontAwesomeIcon 
                                                id={`reinviteIcon${user.id}`}
                                                icon={faEnvelope} 
                                                style={{ marginRight: InlineStylingValues.IconSeparator }}
                                                className={'font-awesome-disabled-icon'}
                                            />
                                        )
                                    }
                                    {
                                        (user.externalAccountId !== loggedInUserExternalId) &&
                                        (
                                            <Tooltip title={`${settingsText?.deleteUserTooltipPrefix || ''} (${user.displayName})`}>
                                                <FontAwesomeIcon
                                                    icon={faTrash}
                                                    id={`deleteIcon${user.id}`}
                                                    className={'actionable-icon'}
                                                    onClick={() => { setConfirmDelete(user) }}
                                                    style={{ marginRight: InlineStylingValues.IconSeparator }}
                                                />
                                            </Tooltip>
                                        )
                                    }
                                    {
                                        (user.externalAccountId === loggedInUserExternalId) &&
                                        (
                                            <FontAwesomeIcon
                                                icon={faTrash}
                                                id={`deleteIcon${user.id}`}
                                                className={'font-awesome-disabled-icon'}
                                                style={{ marginRight: InlineStylingValues.IconSeparator }}
                                            />
                                        )
                                    }
                                </div>
                            )
                        }
                    },
                    {
                        defaultSortOrder: 'ascending',
                        headerTitle: settingsText?.usersManagementTitle || '',
                        compare: (u1: DetailedUserResponse, u2: DetailedUserResponse) => {
                            const fullUserName1 = FullUserName(u1).toLowerCase();
                            const fullUserName2 = FullUserName(u2).toLowerCase();

                            return fullUserName1 === fullUserName2 ? 0 : fullUserName1 < fullUserName2 ? -1 : 1;
                        },
                        getRowData: (user: DetailedUserResponse): React.ReactElement => {
                            return getUserNameCell(user);
                        }
                    },
                    {
                        defaultSortOrder: 'ascending',
                        headerTitle: settingsText?.userStatus || '',
                        compare: (u1: DetailedUserResponse, u2: DetailedUserResponse) => {
                            const status1 = GetUserStatusDescription(u1.status, settingsText);
                            const status2 = GetUserStatusDescription(u2.status, settingsText);

                            return status1 === status2 ? 0 : status1 < status2 ? -1 : 1;
                        },
                        getRowData: (user: DetailedUserResponse): React.ReactElement => {
                            return (<>{GetUserStatusDescription(user.status, settingsText)}</>);
                        }
                    },
                    {
                        defaultSortOrder: 'ascending',
                        headerTitle: settingsText?.userPrimaryEmail || '',
                        compare: (u1: DetailedUserResponse, u2: DetailedUserResponse) => {
                            const email1 = u1.emailAddress.toLowerCase() || '';
                            const email2 = u2.emailAddress.toLowerCase() || '';

                            return email1 === email2 ? 0 : email1 < email2 ? -1 : 1;
                        },
                        getRowData: (user: DetailedUserResponse): React.ReactElement => {
                            return (<>{user.emailAddress || ''}</>);
                        }
                    },
                    {
                        defaultSortOrder: 'descending',
                        headerTitle: settingsText?.userLastLogin || '',
                        compare: (u1: DetailedUserResponse, u2: DetailedUserResponse) => {
                            const t1 = new Date(u1.lastActivityTime);
                            const t2 = new Date(u2.lastActivityTime);

                            return t1 === t2 ? 0 : t1 < t2 ? -1 : 1;
                        },
                        getRowData: (user: DetailedUserResponse): React.ReactElement => {
                            const d = new Date(user.lastActivityTime);

                            return (<>{FormatDate(d, formatStringMDYFullMonth)}</>);
                        }
                    },
                    {
                        defaultSortOrder: 'ascending',
                        headerTitle: settingsText?.userRole || '',
                        compare: (u1: DetailedUserResponse, u2: DetailedUserResponse) => {
                            const r1 = getRoleDescription(u1);
                            const r2 = getRoleDescription(u2);

                            if (r1 !== r2) {
                                return r1 < r2 ? -1 : 1;
                            }

                            const fullUserName1 = FullUserName(u1);
                            const fullUserName2 = FullUserName(u2);

                            return fullUserName1 === fullUserName2 ? 0 : fullUserName1 < fullUserName2 ? -1 : 1;
                        },
                        getRowData: (user: DetailedUserResponse): React.ReactElement => {
                            return (<>{getRoleDescription(user)}</>);
                        }
                    }
                ]}
                data={users}
            />
        </>
    );
}
